import styled from "styled-components";
import {fontFamilies, fontWeights, radii} from "theme/tokens";

export const Body = styled.div`
	padding: 0 24px 24px;
`;

export const TermsText = styled.p`
	margin: 0;
	font-size: 13.5px;
	line-height: 1.6;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const DistinctNote = styled.p`
	margin: 14px 0 0;
	padding: 12px 14px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	font-size: 13.5px;
	line-height: 1.5;
	font-weight: 650;
	color: ${({theme}) => theme.tokens.ink};
`;

export const Field = styled.div`
	margin-top: 18px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

export const DetailsCounter = styled.span<{$atLimit: boolean}>`
	align-self: flex-end;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	color: ${({theme, $atLimit}) => ($atLimit ? theme.tokens.errorInk : theme.tokens.ink2)};
`;

export const Failure = styled.div`
	margin-top: 14px;
	padding: 12px 14px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	font-size: 13.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.errorInk};
`;

export const MailtoLink = styled.a`
	font-weight: 700;
	color: ${({theme}) => theme.tokens.linkInk};
	text-decoration: underline;
	text-underline-offset: 2px;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		border-radius: 4px;
	}
`;

export const DialogActions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 20px 24px 24px;

	button {
		min-height: 44px;
	}

	@media (max-width: 480px) {
		align-items: stretch;
		flex-direction: column-reverse;

		button {
			width: 100%;
		}
	}
`;

/** Form submit action; unlike AccountCards.PrimaryButton, its native submit type is not pinned away. */
export const SubmitButton = styled.button`
	height: 40px;
	padding: 0 16px;
	border: none;
	border-radius: ${radii.control};
	font-family: ${fontFamilies.sans};
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition: 0.16s;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	&:hover:not(:disabled):not([aria-disabled="true"]) {
		background: ${({theme}) => theme.tokens.actionFillHover};
	}

	/* A send in flight marks the button aria-disabled rather than disabled, so it keeps keyboard focus
	   (kitecorp/transferify-ui#3079). It looks exactly as unavailable as a disabled one. */
	&:disabled,
	&[aria-disabled="true"] {
		cursor: not-allowed;
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
	}
`;

export const Success = styled.div`
	padding: 8px 24px 24px;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
`;

export const SuccessMark = styled.div`
	width: 56px;
	height: 56px;
	border-radius: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 26px;
	color: ${({theme}) => theme.tokens.teal};
	background: ${({theme}) => theme.tokens.signalTint};
`;

export const SuccessTitle = styled.h3`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(28px, 5.5vw, 34px);
	line-height: 1.05;
	color: ${({theme}) => theme.tokens.ink};
`;

export const SuccessNote = styled.p`
	margin: 0;
	max-width: 360px;
	font-size: 15px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The acknowledgement branch that did NOT happen. Same voice as {@link SuccessNote} — the request did
 * succeed — but boxed and in the error ink, because a reader skimming a success screen must not take
 * "we could not email you a receipt" for one more line of reassurance.
 */
export const AcknowledgementWarning = styled(SuccessNote)`
	width: 100%;
	box-sizing: border-box;
	padding: 12px 14px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.errorInk};
	text-align: left;
`;

/**
 * Generic read-only key/value list (`<dl>`/`<dt>`/`<dd>`). Shared by the success screen's request
 * reference AND, since ui#1152, the pre-submission account/plan pair that answers art. 11^1 alin. (4)(b)
 * — one visual language for "here is a fact you can't edit, only confirm", not two near-identical styles.
 */
export const DetailList = styled.dl`
	width: 100%;
	max-width: 360px;
	box-sizing: border-box;
	margin: 0;
	padding: 12px 14px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	text-align: left;
`;

export const DetailLabel = styled.dt`
	font-size: 12px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const DetailValue = styled.dd`
	margin: 4px 0 0;
	font-family: ${fontFamilies.mono};
	font-size: 13px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink};
`;

/** Second `dt`/`dd` pair inside the same `DetailList` (the account/plan block has two rows). */
export const DetailRow = styled.div`
	& + & {
		margin-top: 10px;
	}
`;

/** Caption under `DetailList` explaining what the shown account address is used for (alin. (4)(c)). */
export const DetailHint = styled.p`
	margin: 8px 0 0;
	font-size: 12px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;
